import React, { useEffect, useState } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { ArrowLeft, Share2, Heart, Waves, Leaf, Sun, Target, Sparkles, Flame, Users, Compass, Palette } from 'lucide-react';
import { getManifestTheme, getAffirmationImage } from '../data/manifestData';
import AffirmationShareMenu from '../components/AffirmationShareMenu';
import ShareSheet from '../components/ShareSheet';
import CheckInModal from '../components/CheckInModal';

const iconMap = { Heart, Waves, Leaf, Sun, Target, Sparkles, Flame, Users, Compass, Palette };

// Public landing page for a single affirmation — anyone who taps a shared
// link lands here. Fully self-contained: theme-matched hero, full body,
// share menu, and a soft CTA back to the app home.
const AffirmationPage = () => {
  const { themeId, index } = useParams();
  const [shareOpen, setShareOpen] = useState(false);
  const [recommendOpen, setRecommendOpen] = useState(false);
  const [checkInOpen, setCheckInOpen] = useState(false);
  const [dbPoster, setDbPoster] = useState(null);

  const theme = getManifestTheme(themeId);
  const affIdx = Math.max(0, Math.min(parseInt(index, 10) || 0, (theme?.affirmations.length || 1) - 1));

  // Branded emblem poster from the DB (manifest-<theme>-<NN>); static fallback below.
  useEffect(() => {
    let alive = true;
    setDbPoster(null);
    if (!theme) return undefined;
    const slug = `manifest-${themeId}-${String(affIdx).padStart(2, '0')}`;
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/content/${slug}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        const u = d?.posterImageUrl || d?.coverImageUrl;
        if (alive && u) setDbPoster(`${u.startsWith('http') ? u : `${process.env.REACT_APP_BACKEND_URL}${u}`}?w=1200`);
      })
      .catch(() => {});
    return () => { alive = false; };
  }, [themeId, affIdx, theme]);

  // Show a gentle post-affirmation check-in after ~20 seconds of dwell time
  // — long enough that the user has actually sat with the words, short
  // enough that we catch them before they scroll away. Resets whenever the
  // affirmation changes (theme + index).
  useEffect(() => {
    setCheckInOpen(false);
    const t = setTimeout(() => setCheckInOpen(true), 20_000);
    return () => clearTimeout(t);
  }, [themeId, index]);

  if (!theme) return <Navigate to="/manifest" replace />;
  const affirmation = theme.affirmations[affIdx];
  const imagePool = theme.images || [];
  const image = dbPoster || getAffirmationImage(themeId, affIdx) || (imagePool.length ? imagePool[affIdx % imagePool.length] : null);
  const Icon = iconMap[theme.icon] || Sparkles;

  return (
    <div className="pb-10 min-h-screen bg-rbr-cream" data-testid={`affirmation-page-${themeId}-${index}`}>
      {/* HERO — full-bleed image with palette-matched overlay + rings */}
      <div className="relative overflow-hidden rounded-b-[2.5rem] shadow-rbr-lift">
        {image && (
          <div className="relative w-full aspect-[4/5] sm:aspect-[16/10]">
            <img src={image} alt={affirmation.title} className="absolute inset-0 w-full h-full object-cover" />
            <div
              className="absolute inset-0"
              style={{ background: `linear-gradient(160deg, ${theme.gradient[0]}77 0%, ${theme.gradient[1]}f4 100%)` }}
              aria-hidden
            />
            {/* Signature rings */}
            <div className="absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full border border-white/15 pointer-events-none" aria-hidden />
            <div className="absolute -top-20 -right-20 w-[300px] h-[300px] rounded-full border border-white/20 pointer-events-none" aria-hidden />
            <div className="absolute top-0 left-0 right-0 h-px rbr-shimmer" />
          </div>
        )}

        {/* Text over hero */}
        <div className="absolute inset-0 flex flex-col p-6">
          <Link
            to="/manifest"
            data-testid="affirmation-back-btn"
            className="w-10 h-10 rounded-full bg-white/15 backdrop-blur-md flex items-center justify-center text-white hover:bg-white/25 transition-colors"
            aria-label="Back to Manifest"
          >
            <ArrowLeft className="w-5 h-5" />
          </Link>

          <div className="mt-auto text-white animate-fade-up">
            <div className="w-12 h-12 rounded-2xl bg-white/20 backdrop-blur-md border border-white/30 flex items-center justify-center mb-4">
              <Icon className="w-6 h-6 text-white" />
            </div>
            <p className="text-[10px] uppercase tracking-[0.28em] text-white/80 font-medium mb-2">
              Manifest · {theme.title}
            </p>
            <h1
              className="text-3xl sm:text-4xl leading-tight mb-2 max-w-[92%]"
              style={{ fontFamily: "'Playfair Display', serif", fontWeight: 600 }}
            >
              {affirmation.title}
            </h1>
            <p
              className="text-white/90 text-base sm:text-lg leading-snug max-w-[92%]"
              style={{ fontFamily: "'Playfair Display', serif", fontStyle: 'italic' }}
            >
              {affirmation.body.split('. ')[0]}.
            </p>
          </div>
        </div>
      </div>

      {/* Body */}
      <article className="px-5 mt-6 max-w-[560px] mx-auto">
        <p className="text-rbr-blue-deep text-[16px] leading-relaxed whitespace-pre-line rbr-serif-italic">
          {affirmation.body}
        </p>

        {/* Share row */}
        <div className="mt-8 flex flex-col items-center gap-3">
          <button
            onClick={() => setShareOpen(true)}
            data-testid="affirmation-share-btn"
            className="rbr-btn-gold px-8 py-3 text-sm"
          >
            <Share2 className="w-4 h-4" />
            Share this affirmation
          </button>
          <button
            onClick={() => setRecommendOpen(true)}
            data-testid="affirmation-recommend-btn"
            className="text-[11px] uppercase tracking-[0.22em] text-rbr-gold-deep font-semibold hover:text-rbr-blue transition-colors inline-flex items-center gap-1.5"
          >
            <Users className="w-3.5 h-3.5" /> Recommend to someone
          </button>
          <Link
            to="/"
            className="text-[11px] uppercase tracking-[0.22em] text-rbr-gold-deep font-semibold hover:text-rbr-blue transition-colors"
          >
            Open Reflections by Rowdy
          </Link>
        </div>

        <p className="rbr-serif-italic text-center text-rbr-sage-deep text-sm mt-10 leading-relaxed">
          Repeat it gently, three times, before you move on with your day.
        </p>
      </article>

      {shareOpen && (
        <AffirmationShareMenu
          theme={theme}
          affirmation={affirmation}
          index={affIdx}
          image={image}
          eyebrow={`Manifest · ${theme.title}`}
          onClose={() => setShareOpen(false)}
        />
      )}

      <ShareSheet
        open={recommendOpen}
        onClose={() => setRecommendOpen(false)}
        content={{
          kind: 'affirmation',
          id: `${theme.id}-${affIdx}`,
          title: affirmation.title,
          text: `"${affirmation.title}" — a manifest affirmation from Reflections by Rowdy`,
          url: `${window.location.origin}/a/${theme.id}/${affIdx}`,
          recommendable: true,
        }}
      />

      <CheckInModal
        open={checkInOpen}
        onClose={() => setCheckInOpen(false)}
        contentRef={`affirmation:${themeId}:${affIdx}`}
        title="How are you feeling after this affirmation?"
      />
    </div>
  );
};

export default AffirmationPage;
